import { Size } from '@front-app/components/constants';
import { TextAlignment } from '@front-app/components/inputs/alignmentButtons';
import { VideoProvider } from '@front-app/components/video/util';
import { v4 } from 'uuid';
import {
	ElementType,
	DraggablePlaceholder,
	HighlightsSectionRequest,
	SectionType,
	HighlightsButtonStyle,
	SurveyProvider,
	AnyHighlightComponentRequest,
	HighlightsImageComponentRequest,
	HighlightsSurveyComponentRequest,
	HighlightsVideoComponentRequest,
	HighlightsHeadingComponentRequest,
	HighlightsRichTextComponentRequest,
	HighlightsImageSize,
	HIGHLIGHTS_IMAGE_RATIOS,
	UnfilledHighlightsButtonComponent,
} from '../model';
import { createSection } from './create';

/**
 * Converts a section placeholder into an actual section, to be inserted into a Highlights section list
 *
 * @param sectionPlaceholder
 */
export function cloneSection(sectionPlaceholder: DraggablePlaceholder<SectionType>): HighlightsSectionRequest {
	return createSection(sectionPlaceholder.type);
}

const TEXT_FOR_ELEMENT: Record<ElementType, string> = {
	[ElementType.HEADING]: 'A Heading',
	[ElementType.TEXT]: 'Enter your text here...',
	[ElementType.BUTTON]: 'Click Here',
	[ElementType.IMAGE]: '',
	[ElementType.SURVEY]: '',
	[ElementType.VIDEO]: '',
};

/**
 * Converts an element placeholder into an actual element, to be inserted into a HighlightsSection's "elements"
 *
 * @param elementPlaceholder
 */
export function cloneElement(elementPlaceholder: DraggablePlaceholder<ElementType>): AnyHighlightComponentRequest | UnfilledHighlightsButtonComponent {
	switch (elementPlaceholder.type) {
	case ElementType.HEADING:
		return cloneHeading();
	case ElementType.TEXT:
		return cloneText();
	case ElementType.BUTTON:
		return cloneButton();
	case ElementType.IMAGE:
		return cloneImage();
	case ElementType.SURVEY:
		return cloneSurvey();
	case ElementType.VIDEO:
		return cloneVideo();
	default:
		// eslint-disable-next-line
		const exhaustiveCheck: never = elementPlaceholder.type;
		throw new Error(`Unhandled placeholder case: ${exhaustiveCheck}`);
	}
}

const { ratioWidth, ratioHeight } = HIGHLIGHTS_IMAGE_RATIOS[HighlightsImageSize.BANNER_MEDIUM];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function cloneImage(): HighlightsImageComponentRequest {
	return {
		newId: v4(),
		type: ElementType.IMAGE,
		meta: {
			href: '',
			altText: '',
			src: '',
			ratioWidth,
			ratioHeight,
		},
	};
}

function cloneSurvey(): HighlightsSurveyComponentRequest {
	return {
		newId: v4(),
		type: ElementType.SURVEY,
		meta: {
			src: '',
			type: SurveyProvider.JOTFORM,
			surveyId: '',
		},
	};
}

function cloneVideo(): HighlightsVideoComponentRequest {
	return {
		newId: v4(),
		type: ElementType.VIDEO,
		meta: {
			src: '',
			type: VideoProvider.UNKNOWN,
			videoId: '',
		},
	};
}

function cloneButton(): UnfilledHighlightsButtonComponent {
	return {
		newId: v4(),
		type: ElementType.BUTTON,
		meta: {
			size: Size.LARGE,
			mainText: TEXT_FOR_ELEMENT[ElementType.BUTTON],
			action: null,
			style: HighlightsButtonStyle.FILLED,
			href: 'https://',
		},
	};
}

function cloneText(): HighlightsRichTextComponentRequest {
	return {
		newId: v4(),
		type: ElementType.TEXT,
		meta: {
			mainText: TEXT_FOR_ELEMENT[ElementType.TEXT],
		},
	};
}

function cloneHeading(): HighlightsHeadingComponentRequest {
	return {
		newId: v4(),
		type: ElementType.HEADING,
		meta: {
			mainText: TEXT_FOR_ELEMENT[ElementType.HEADING],
			alignment: TextAlignment.LEFT,
			size: Size.LARGE,
		},
	};
}
